import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  AlertCircle,
  Calendar,
  Users,
  Target,
  TrendingUp,
  BarChart3,
  List,
  Activity,
  DollarSign,
  FileText,
  ChevronDown,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import { useBrandDeals, DealTask } from '@/hooks/useBrandDeals';
import { usePaymentSchedules } from '@/hooks/usePaymentSchedules';
import { useDeliverables } from '@/hooks/useDeliverables';
import { GanttChart } from '@/components/deals/GanttChart';
import { LoadingFallback } from '@/components/LoadingFallback';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';


interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  type: 'task' | 'payment' | 'deliverable';
  status: string;
  startDate?: string;
  dueDate?: string;
  assignedTo?: string;
  dealId?: string;
  dealTitle?: string;
  campaignId?: string;
  campaignName?: string;
  amount?: number;
  priority?: string;
  progress?: number;
  payment_status?: string;
}

export const BrandTimelineTasksOverview = () => {
  const { deals, campaigns, loading, error, updateTask } = useBrandDeals();
  const { paymentSchedules, loading: paymentsLoading, fetchPaymentSchedules } = usePaymentSchedules();
  const { deliverables, loading: deliverablesLoading, fetchDeliverables } = useDeliverables();
  
  const [activeView, setActiveView] = useState('timeline');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewType, setViewType] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState('all');
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());

  // Helper to toggle deal expansion
  const toggleDeal = (dealId: string) => {
    setExpandedDeals(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  };

  // Helper to expand all deals
  const expandAll = () => {
    const allDealIds = new Set(filteredItems.map(item => item.dealId).filter(Boolean) as string[]);
    setExpandedDeals(allDealIds);
  };

  // Helper to collapse all deals
  const collapseAll = () => {
    setExpandedDeals(new Set());
  };

  // Fetch payment schedules and deliverables once on mount
  useEffect(() => {
    fetchPaymentSchedules();
    fetchDeliverables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate all timeline items from deals, payments, and deliverables
  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = [];

    // Add tasks from deals
    deals.forEach(deal => {
      const campaign = campaigns.find(c => c.id === deal.campaign_id);
      const taskList = (deal as any).tasks || [];
      
      if (taskList && taskList.length > 0) {
        (taskList as DealTask[]).forEach(task => {
          items.push({
            id: task.id,
            title: task.title,
            description: task.description,
            type: 'task',
            status: task.status || 'pending',
            startDate: task.start_date,
            dueDate: task.due_date,
            assignedTo: task.assigned_to,
            dealId: deal.id,
            dealTitle: deal.title || 'Untitled Deal',
            campaignId: campaign?.id,
            campaignName: campaign?.name,
            priority: 'medium',
            progress: task.progress_percentage,
            amount: task.payment_amount || 0,
            payment_status: task.payment_status
          });
        });
      }
    });

    // Add payment schedules
    paymentSchedules.forEach(payment => {
      const deal = deals.find(d => d.id === payment.deal_id);
      const campaign = campaigns.find(c => c.id === deal?.campaign_id);
      items.push({
        id: payment.id,
        title: payment.milestone_name,
        description: `Payment of $${payment.amount}`,
        type: 'payment',
        status: payment.status,
        dueDate: payment.due_date,
        dealId: payment.deal_id,
        dealTitle: deal?.title || 'Untitled Deal',
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        amount: Number(payment.amount),
        priority: payment.status === 'overdue' ? 'high' : 'medium'
      });
    });

    // Add deliverables
    deliverables.forEach(deliverable => {
      const deal = deals.find(d => d.id === deliverable.deal_id);
      const campaign = campaigns.find(c => c.id === deliverable.campaign_id || c.id === deal?.campaign_id);
      items.push({
        id: deliverable.id,
        title: deliverable.title,
        description: deliverable.description,
        type: 'deliverable',
        status: deliverable.status,
        dueDate: deliverable.due_date,
        dealId: deliverable.deal_id,
        dealTitle: deal?.title || 'Untitled Deal',
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        priority: new Date(deliverable.due_date) < new Date() && !['approved', 'submitted'].includes(deliverable.status) ? 'high' : 'medium'
      });
    });
    
    return items.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
  }, [deals, campaigns, paymentSchedules, deliverables]);

  // Filter timeline items
  const filteredItems = useMemo(() => {
    return timelineItems.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.dealTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.campaignName || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesType = typeFilter === 'all' || item.type === typeFilter;
      
      let matchesView = true;
      if (viewType === 'campaign' && selectedCampaign !== 'all') {
        matchesView = item.campaignId === selectedCampaign;
      } else if (viewType === 'deal' && selectedDeal !== 'all') {
        matchesView = item.dealId === selectedDeal;
      }
      
      return matchesSearch && matchesStatus && matchesType && matchesView;
    });
  }, [timelineItems, searchTerm, statusFilter, typeFilter, viewType, selectedCampaign, selectedDeal]);

  // Calculate timeline metrics
  const timelineMetrics = useMemo(() => {
    const total = timelineItems.length;
    const completed = timelineItems.filter(item => {
      if (item.type === 'task') return item.status === 'completed';
      if (item.type === 'payment') return item.status === 'paid';
      if (item.type === 'deliverable') return ['approved', 'submitted'].includes(item.status);
      return false;
    }).length;
    const pending = timelineItems.filter(item => item.status === 'pending').length;
    const overdue = timelineItems.filter(item => {
      if (!item.dueDate) return false;
      const dueDate = new Date(item.dueDate);
      const today = new Date();
      if (item.type === 'task') return dueDate < today && item.status !== 'completed';
      if (item.type === 'payment') return dueDate < today && item.status !== 'paid';
      if (item.type === 'deliverable') return dueDate < today && !['approved', 'submitted'].includes(item.status);
      return false;
    }).length;

    const totalValue = timelineItems
      .filter(item => item.amount)
      .reduce((sum, item) => sum + (item.amount || 0), 0);

    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    const byType = {
      tasks: timelineItems.filter(item => item.type === 'task').length,
      payments: timelineItems.filter(item => item.type === 'payment').length,
      deliverables: timelineItems.filter(item => item.type === 'deliverable').length
    };

    return {
      total,
      completed,
      pending,
      overdue,
      totalValue,
      completionRate,
      byType
    };
  }, [timelineItems]);


  // Get unique campaign and deal options for filters
  const campaignOptions = useMemo(() => campaigns.map(c => ({ id: c.id, name: c.name })), [campaigns]);
  const dealOptions = useMemo(() => deals.map(d => ({ id: d.id, title: d.title || 'Untitled Deal' })), [deals]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'in_progress':
        return 'bg-primary/10 text-primary';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleMarkComplete = async (itemId: string, itemType: string) => {
    try {
      if (itemType === 'task') {
        await updateTask(itemId, { status: 'completed', progress_percentage: 100 });
        toast.success('Task marked as complete');
      }
      // Add handlers for payments and deliverables when needed
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update task');
    }
  };

  const handlePayTask = async (item: TimelineItem) => {
    if (!item.amount || item.amount <= 0) {
      toast.error('No payment amount set for this task');
      return;
    }

    if (!confirm(`Confirm payment of $${item.amount.toLocaleString()} for "${item.title}"?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('pay-task', {
        body: { taskId: item.id }
      });

      if (error) throw error;

      toast.success(`Payment of $${item.amount.toLocaleString()} completed successfully`);
      
      // Refresh deals to update the UI
      window.location.reload();
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to process payment');
    }
  };

  const getStatusBadge = (status: string, type: string) => {
    const isCompleted = (type === 'task' && status === 'completed') ||
                       (type === 'payment' && status === 'paid') ||
                       (type === 'deliverable' && ['approved', 'submitted'].includes(status));

    if (isCompleted) {
      return (
        <Badge className="bg-success/10 text-success border-success/20 font-medium">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    } else if (status === 'pending') {
      return (
        <Badge className="bg-warning/10 text-warning border-warning/20 font-medium">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    } else if (status === 'overdue') {
      return (
        <Badge className="bg-destructive/10 text-destructive border-destructive/20 font-medium">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Overdue
        </Badge>
      );
    } else if (['in_progress', 'processing'].includes(status)) {
      return (
        <Badge className="bg-primary/10 text-primary border-primary/20 font-medium">
          <Activity className="h-3 w-3 mr-1" />
          In Progress
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="font-medium capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'task': return <CheckCircle2 className="h-4 w-4" />;
      case 'payment': return <DollarSign className="h-4 w-4" />;
      case 'deliverable': return <FileText className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const renderTimelineList = () => {
    // Group items by deal
    const itemsByDeal = new Map<string, {
      dealId: string;
      dealTitle: string;
      campaignName?: string;
      items: TimelineItem[];
    }>();

    filteredItems.forEach(item => {
      const dealId = item.dealId || 'no-deal';
      const dealTitle = item.dealTitle || 'Unassigned Items';
      
      if (!itemsByDeal.has(dealId)) {
        itemsByDeal.set(dealId, {
          dealId,
          dealTitle,
          campaignName: item.campaignName,
          items: []
        });
      }
      
      itemsByDeal.get(dealId)!.items.push(item);
    });

    // Sort deals by title
    const sortedDeals = Array.from(itemsByDeal.values()).sort((a, b) => 
      a.dealTitle.localeCompare(b.dealTitle)
    );

    // Calculate metrics for each deal
    const dealMetrics = new Map<string, {
      total: number;
      completed: number;
      overdue: number;
      pending: number;
      completionRate: number;
    }>();

    sortedDeals.forEach(deal => {
      const total = deal.items.length;
      const completed = deal.items.filter(item => {
        if (item.type === 'task') return item.status === 'completed';
        if (item.type === 'payment') return item.status === 'paid';
        if (item.type === 'deliverable') return ['approved', 'submitted'].includes(item.status);
        return false;
      }).length;
      
      const overdue = deal.items.filter(item => {
        if (!item.dueDate) return false;
        const dueDate = new Date(item.dueDate);
        const today = new Date();
        if (item.type === 'task') return dueDate < today && item.status !== 'completed';
        if (item.type === 'payment') return dueDate < today && item.status !== 'paid';
        if (item.type === 'deliverable') return dueDate < today && !['approved', 'submitted'].includes(item.status);
        return false;
      }).length;

      const pending = deal.items.filter(item => item.status === 'pending').length;
      const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

      dealMetrics.set(deal.dealId, {
        total,
        completed,
        overdue,
        pending,
        completionRate
      });
    });

    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Timeline by Project</CardTitle>
              <CardDescription>
                Organized by deal/project {filteredItems.length !== timelineItems.length && `(${filteredItems.length} of ${timelineItems.length})`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAll}
                className="text-xs"
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAll}
                className="text-xs"
              >
                Collapse All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground font-medium">No timeline items match your filters</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedDeals.map((deal) => {
                const metrics = dealMetrics.get(deal.dealId)!;
                const isExpanded = expandedDeals.has(deal.dealId);
                
                return (
                  <Collapsible
                    key={deal.dealId}
                    open={isExpanded}
                    onOpenChange={() => toggleDeal(deal.dealId)}
                  >
                    {/* Deal Header */}
                    <div className={`border rounded-lg transition-all duration-200 ${
                      isExpanded ? 'border-primary/40 shadow-md' : 'border-border hover:border-primary/20 hover:shadow-sm'
                    }`}>
                      <CollapsibleTrigger asChild>
                        <div className="w-full p-4 cursor-pointer group">
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Deal Info */}
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className={`flex items-center justify-center shrink-0 transition-transform duration-200 ${
                                isExpanded ? 'rotate-0' : ''
                              }`}>
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-primary" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-lg text-foreground truncate">
                                    {deal.dealTitle}
                                  </h3>
                                  {deal.campaignName && (
                                    <Badge variant="secondary" className="text-xs shrink-0">
                                      {deal.campaignName}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Deal Metrics */}
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Activity className="h-3 w-3" />
                                    {metrics.total} items
                                  </span>
                                  <span className="flex items-center gap-1 text-success">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {metrics.completed} completed
                                  </span>
                                  {metrics.pending > 0 && (
                                    <span className="flex items-center gap-1 text-warning">
                                      <Clock className="h-3 w-3" />
                                      {metrics.pending} pending
                                    </span>
                                  )}
                                  {metrics.overdue > 0 && (
                                    <span className="flex items-center gap-1 text-destructive font-medium">
                                      <AlertTriangle className="h-3 w-3" />
                                      {metrics.overdue} overdue
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Right: Completion Rate */}
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-right">
                                <div className="text-lg font-bold text-foreground">
                                  {metrics.completionRate}%
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  complete
                                </div>
                              </div>
                              <div className="w-16 h-16">
                                <svg className="transform -rotate-90" viewBox="0 0 100 100">
                                  <circle
                                    cx="50"
                                    cy="50"
                                    r="40"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    className="text-muted opacity-20"
                                  />
                                  <circle
                                    cx="50"
                                    cy="50"
                                    r="40"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    strokeDasharray={`${metrics.completionRate * 2.51} 251`}
                                    className={`transition-all duration-500 ${
                                      metrics.completionRate === 100 ? 'text-success' :
                                      metrics.completionRate >= 50 ? 'text-primary' :
                                      'text-warning'
                                    }`}
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* Deal Items (when expanded) */}
                      <CollapsibleContent>
                        <div className="border-t p-4 bg-muted/20">
                          <div className="space-y-3">
                            {deal.items
                              .sort((a, b) => {
                                if (!a.dueDate) return 1;
                                if (!b.dueDate) return -1;
                                return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                              })
                              .map((item) => {
                                const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && 
                                  !((item.type === 'task' && item.status === 'completed') ||
                                    (item.type === 'payment' && item.status === 'paid') ||
                                    (item.type === 'deliverable' && ['approved', 'submitted'].includes(item.status)));
                                
                                return (
                                  <div key={item.id} className="animate-fade-in">
                                    <div className={`relative bg-card border rounded-lg p-4 transition-all duration-200 hover:shadow-lg hover:border-primary/20 hover:-translate-y-0.5 ${
                                      isOverdue ? 'border-l-4 border-l-destructive' : ''
                                    }`}>
                                      {/* Header */}
                                      <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                          <div className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${
                                            item.type === 'task' ? 'bg-primary/10 text-primary' :
                                            item.type === 'payment' ? 'bg-success/10 text-success' :
                                            'bg-accent text-accent-foreground'
                                          }`}>
                                            {getTypeIcon(item.type)}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <h5 className="font-semibold text-base text-foreground mb-1 truncate">{item.title}</h5>
                                            {item.description && (
                                              <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {/* Status Badge */}
                                        <div className="shrink-0">
                                          {getStatusBadge(item.status, item.type)}
                                        </div>
                                      </div>

                                      {/* Progress Bar */}
                                      {item.type === 'task' && typeof item.progress === 'number' && (
                                        <div className="mb-3">
                                          <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-muted-foreground">Progress</span>
                                            <span className="font-medium text-foreground">{item.progress}%</span>
                                          </div>
                                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                            <div 
                                              className="h-full bg-primary transition-all duration-300 rounded-full"
                                              style={{ width: `${item.progress}%` }}
                                            />
                                          </div>
                                        </div>
                                      )}

                                      {/* Metadata */}
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground mb-3">
                                        <Badge variant="secondary" className="text-xs font-normal capitalize">
                                          {item.type}
                                        </Badge>
                                        {item.startDate && (
                                          <span className="flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            Start: {formatDate(item.startDate)}
                                          </span>
                                        )}
                                        {item.dueDate && (
                                          <span className={`flex items-center gap-1 font-medium ${isOverdue ? 'text-destructive' : ''}`}>
                                            <Calendar className="h-3 w-3" />
                                            Due: {formatDate(item.dueDate)}
                                          </span>
                                        )}
                                        {item.type === 'task' && (
                                          <span className={`flex items-center gap-1 font-semibold ${
                                            item.amount && item.amount > 0 ? 'text-success' : 'text-muted-foreground'
                                          }`}>
                                            <DollarSign className="h-3 w-3" />
                                            {item.amount && item.amount > 0 
                                              ? `$${item.amount.toLocaleString()}` 
                                              : 'No amount set'}
                                          </span>
                                        )}
                                      </div>
                                      
                  {/* Payment Status Badges */}
                  {item.type === 'task' && (
                    <div className="flex items-center gap-2 mt-2">
                      {/* Badges when amount is configured */}
                      {item.amount && item.amount > 0 && item.payment_status === 'paid' && (
                        <Badge className="bg-green-600 text-white">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Paid
                        </Badge>
                      )}
                      {item.amount && item.amount > 0 && item.payment_status === 'due' && (
                        <Badge className="bg-orange-600 text-white">
                          <Clock className="h-3 w-3 mr-1" />
                          Payment Due
                        </Badge>
                      )}
                      {item.amount && item.amount > 0 && item.payment_status === 'unpaid' && item.status === 'completed' && (
                        <Badge className="bg-yellow-600 text-white">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Unpaid
                        </Badge>
                      )}
                      
                      {/* Badge when amount is not configured */}
                      {(!item.amount || item.amount <= 0) && (
                        <Badge variant="outline" className="border-orange-500 text-orange-600">
                          <DollarSign className="h-3 w-3 mr-1" />
                          Payment not configured
                        </Badge>
                      )}
                    </div>
                  )}

                                      {/* Action Buttons */}
                                      <div className="mt-3 flex items-center gap-2">
                                        {/* Pay Now button for completed tasks with amount configured */}
                                        {item.type === 'task' && 
                                         item.status === 'completed' && 
                                         item.payment_status === 'due' && 
                                         item.amount && 
                                         item.amount > 0 && (
                                          <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => handlePayTask(item)}
                                            className="bg-green-600 hover:bg-green-700 text-white"
                                          >
                                            <DollarSign className="h-4 w-4 mr-1" />
                                            Pay Now
                                          </Button>
                                        )}
                                        
                                        {/* Set Payment Amount button for tasks with no amount */}
                                        {item.type === 'task' && (!item.amount || item.amount <= 0) && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              toast.info('Go to the Payments tab to set the payment amount for this task');
                                            }}
                                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                          >
                                            <DollarSign className="h-4 w-4 mr-1" />
                                            Set Payment Amount
                                          </Button>
                                        )}
                                        
                                        {/* Complete button for incomplete tasks */}
                                        {item.type === 'task' && item.status !== 'completed' && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleMarkComplete(item.id, item.type)}
                                          >
                                            <CheckCircle2 className="h-4 w-4 mr-1" />
                                            Complete
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Group tasks by deal for Gantt view
  const tasksByDeal = useMemo(() => {
    const itemsByDeal = new Map<string, {
      dealId: string;
      dealTitle: string;
      campaignName?: string;
      tasks: TimelineItem[];
    }>();
    
    // Filter for tasks only and group by deal
    filteredItems
      .filter(item => item.type === 'task' && item.dueDate)
      .forEach(item => {
        const dealId = item.dealId || 'no-deal';
        const dealTitle = item.dealTitle || 'Unassigned Tasks';
        
        if (!itemsByDeal.has(dealId)) {
          itemsByDeal.set(dealId, {
            dealId,
            dealTitle,
            campaignName: item.campaignName,
            tasks: []
          });
        }
        
        itemsByDeal.get(dealId)!.tasks.push(item);
      });
    
    // Sort deals by title
    return Array.from(itemsByDeal.values()).sort((a, b) => 
      a.dealTitle.localeCompare(b.dealTitle)
    );
  }, [filteredItems]);

  // Calculate metrics for each deal's tasks
  const taskDealMetrics = useMemo(() => {
    const metrics = new Map<string, {
      total: number;
      completed: number;
      overdue: number;
      pending: number;
      completionRate: number;
    }>();
    
    tasksByDeal.forEach(deal => {
      const total = deal.tasks.length;
      const completed = deal.tasks.filter(t => t.status === 'completed').length;
      const overdue = deal.tasks.filter(t => {
        if (!t.dueDate) return false;
        return new Date(t.dueDate) < new Date() && t.status !== 'completed';
      }).length;
      const pending = deal.tasks.filter(t => t.status === 'pending').length;
      const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
      
      metrics.set(deal.dealId, {
        total,
        completed,
        overdue,
        pending,
        completionRate
      });
    });
    
    return metrics;
  }, [tasksByDeal]);

  console.log('BrandTimelineTasksOverview render - Loading states:', {
    loading, 
    paymentsLoading, 
    deliverablesLoading,
    deals: deals.length,
    paymentSchedules: paymentSchedules.length,
    deliverables: deliverables.length
  });

  if (loading || paymentsLoading || deliverablesLoading) {
    return <LoadingFallback />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-destructive">Error loading timeline data: {error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Timeline & Tasks</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Track and manage all activities across your deals and campaigns
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-primary hover:shadow-md transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Items</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{timelineMetrics.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all deals and campaigns
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success hover:shadow-md transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completion Rate</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-success" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{timelineMetrics.completionRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {timelineMetrics.completed} of {timelineMetrics.total} completed
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-warning hover:shadow-md transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-warning" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{timelineMetrics.pending}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Awaiting action or approval
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-destructive hover:shadow-md transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Overdue</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{timelineMetrics.overdue}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Requires immediate attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, deal, or campaign..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-10 border-muted-foreground/20 focus:border-primary"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-10">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px] h-10">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="task">Tasks</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
                <SelectItem value="deliverable">Deliverables</SelectItem>
              </SelectContent>
            </Select>

            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-[160px] h-10">
                <SelectValue placeholder="View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items</SelectItem>
                <SelectItem value="campaign">By Campaign</SelectItem>
                <SelectItem value="deal">By Deal</SelectItem>
              </SelectContent>
            </Select>

            {viewType === 'campaign' && (
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger className="w-[200px] h-10">
                  <SelectValue placeholder="Select Campaign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaignOptions.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {viewType === 'deal' && (
              <Select value={selectedDeal} onValueChange={setSelectedDeal}>
                <SelectTrigger className="w-[200px] h-10">
                  <SelectValue placeholder="Select Deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Deals</SelectItem>
                  {dealOptions.map(deal => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
        <TabsList className="bg-muted/50 p-1 h-11">
          <TabsTrigger value="timeline" className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Timeline</span>
          </TabsTrigger>
          <TabsTrigger value="gantt" className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Gantt</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-4">
          {renderTimelineList()}
        </TabsContent>

        <TabsContent value="gantt" className="space-y-4">
          {tasksByDeal.length > 0 ? (
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b bg-muted/30 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Gantt Chart by Deal</CardTitle>
                    <CardDescription>Visual timeline of tasks grouped by deal</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedDeals(new Set(tasksByDeal.map(d => d.dealId)))}
                    >
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Expand All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedDeals(new Set())}
                    >
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Collapse All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                {tasksByDeal.map(deal => {
                  const metrics = taskDealMetrics.get(deal.dealId);
                  if (!metrics) return null;
                  
                  return (
                    <Collapsible
                      key={deal.dealId}
                      open={expandedDeals.has(deal.dealId)}
                      onOpenChange={() => toggleDeal(deal.dealId)}
                    >
                      <div className="border border-border/60 rounded-lg hover:border-primary/30 transition-colors">
                        <CollapsibleTrigger asChild>
                          <button className="w-full">
                            <div className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                              <div className="flex items-center gap-3 flex-1">
                                {expandedDeals.has(deal.dealId) ? (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                                )}
                                <div className="text-left">
                                  <h4 className="font-semibold text-sm text-foreground">{deal.dealTitle}</h4>
                                  {deal.campaignName && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {deal.campaignName}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Activity className="h-3 w-3" />
                                    {metrics.total} tasks
                                  </span>
                                  <span className="flex items-center gap-1 text-success">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {metrics.completed} completed
                                  </span>
                                  <span className="flex items-center gap-1 text-warning">
                                    <Clock className="h-3 w-3" />
                                    {metrics.pending} pending
                                  </span>
                                  {metrics.overdue > 0 && (
                                    <span className="flex items-center gap-1 text-destructive">
                                      <AlertCircle className="h-3 w-3" />
                                      {metrics.overdue} overdue
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-muted-foreground">{metrics.completionRate}%</span>
                                  <svg className="w-10 h-10 -rotate-90">
                                    <circle
                                      cx="20"
                                      cy="20"
                                      r="16"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                      fill="none"
                                      className="text-muted/30"
                                    />
                                    <circle
                                      cx="20"
                                      cy="20"
                                      r="16"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                      fill="none"
                                      strokeDasharray={`${2 * Math.PI * 16}`}
                                      strokeDashoffset={`${2 * Math.PI * 16 * (1 - metrics.completionRate / 100)}`}
                                      className="text-primary transition-all duration-300"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-4 pb-4 pt-2 border-t bg-muted/10">
                            <GanttChart tasks={deal.tasks.map(item => ({
                              id: item.id,
                              deal_id: item.dealId || '',
                              title: item.title,
                              description: item.description || '',
                              status: item.status,
                              assigned_to: item.assignedTo || 'brand',
                              due_date: item.dueDate!,
                              completion_date: null,
                              progress_percentage: item.progress ?? 0,
                              depends_on_task_id: null,
                              created_at: '',
                              updated_at: '',
                              is_milestone: false,
                              deliverable_id: null
                            } as DealTask))} />
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Activity className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">No tasks available</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Tasks will appear here once created</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};